import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import OtpModal from "../../common/components/OtpModal";
import {
  GRIEVANCE_QUERY_TYPE_OPTIONS,
  CITIZENSHIP_OPTIONS,
} from "./constants/formOptions";
import {
  verifyLoanAccount,
  sendMobileOtp,
  sendEmailOtp,
  verifyOtp,
  submitGrievance,
  trackGrievanceBySRN,
  getTrancheAndBankDetails,
} from "./services/grievanceService";
import { CaptchaSection } from "../../common/components";
import {getCleanErrorMessage} from "../../common/config";

// Import shared styles
import "../../common/styles/index.css";
import "./styles/grievance-form.css";

// AFTER useState declarations

const params =
    typeof globalThis === "undefined"
        ? new URLSearchParams()
        : new URLSearchParams(globalThis.location.search);

const encodedCaseIdFromUrl = params.get("srn");
const emailRegex = new RegExp(import.meta.env.VITE_EMAIL_REGEX);
const isValidMobile = (mobile) => /^[6-9]\d{9}$/.test(mobile);
const caseIdFromUrl = encodedCaseIdFromUrl ? atob(encodedCaseIdFromUrl) : null;
const WRITE_TO_US_PAGE_URL = "write-to-us";
const validatePersonalDetails = (formData, newErrors) => {
  if (!formData.firstName.trim()) {
    newErrors.firstName = "First name is required";
  }
  if (!formData.lastName.trim()) {
    newErrors.lastName = "Last name is required";
  }
  return newErrors;
};

const validateContactDetails = (formData, newErrors) => {
  const isMobileValid = isValidMobile(formData.mobileNumber);
  const isEmailValid = emailRegex.test(formData.email);

  // Both fields mandatory
  if (!formData.mobileNumber) {
    newErrors.mobileNumber = "Mobile number is required";
  }

  if (!formData.email) {
    newErrors.email = "Email is required";
  }

    // Individual validation (NEW)
    if (formData.mobileNumber && !isMobileValid) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    }

    if (formData.email && !isEmailValid) {
      newErrors.email = "Please enter a valid email address";
    }
    return newErrors;
};

const validateCaptchaAndOtp = (isCaptchaValid, isOtpVerified, newErrors) => {
  if (!isCaptchaValid) {
    newErrors.captcha = "Please enter the correct captcha";
  }
  if (!isOtpVerified) {
    newErrors.otp = "Please verify OTP";
  }
  return newErrors;
};

const validateGrievanceDetails = (formData, newErrors) => {
  if (!formData.queryType) {
    newErrors.queryType = "Please select a query type";
  }
  if (!formData.grievanceMessage.trim()) {
    newErrors.grievanceMessage = "Please describe your grievance";
  }
  return newErrors;
};

const formatTime = (seconds) => {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}:${sec < 10 ? "0" : ""}${sec}`;
};
const SuccessView = ({ onReset }) => (
    <div className="scl-grievance-form scl-grievance-form--success">
      <div className="scl-grievance-form__success-icon">✓</div>
      <h2>Grievance Submitted Successfully!</h2>
      <p>
        Your grievance has been registered. You will receive a service request
        number via email/SMS shortly.
      </p>

      <button
          type="button"
          className="scl-form-btn scl-form-btn--primary"
          onClick={onReset}
      >
        Submit Another Grievance
      </button>
    </div>
);
SuccessView.propTypes = {
  onReset: PropTypes.func.isRequired,
};


function GrievanceFormApp({ siteId }) {
  const [requestedBefore, setRequestedBefore] = useState(
      caseIdFromUrl ? "yes" : null,
  );
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const [hasLoanAccount, setHasLoanAccount] = useState(null);
  const [loanAccountNo, setLoanAccountNo] = useState("");
  const [isLoanVerified, setIsLoanVerified] = useState(false);
  const [loanVerifyLoading, setLoanVerifyLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    citizenship: "indian",
    queryType: "",
    grievanceMessage: "",
    attachmentFile: null,
    serviceRequestNumber: caseIdFromUrl || ""
  });

  const [captchaInput, setCaptchaInput] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [otpTarget, setOtpTarget] = useState(null);

  // Bank and Account No
  const [bankName, setBankName] = useState("");
  const [accountNo, setAccountNo] = useState("");

  //resend opt
  const [mobileOtpTimer, setMobileOtpTimer] = useState(0);
  const [emailOtpTimer, setEmailOtpTimer] = useState(0);
  const [showOtpModal, setShowOtpModal] = useState(false);
  useEffect(() => {
    if (!caseIdFromUrl) return;

    setPrefillLoading(true);
    setGlobalError("");

    trackGrievanceBySRN(caseIdFromUrl)
        .then((res) => {
          const payload = res?.Payload;
          if (!payload) throw new Error("Unable to fetch grievance details. Payload missing from API response.");

          const emailDetails = payload.emailDetailsByCaseId?.[0] || {};

          setFormData((prev) => ({
            ...prev,
            firstName: emailDetails["contact.firstname"] || "",
            lastName: emailDetails["contact.lastname"] || "",
            mobileNumber: emailDetails["contact.mobilephone"] || "",
            email: emailDetails["contact.emailaddress1"] || "",
            grievanceMessage: payload.caseDetailsByCaseId?.title || "",
          }));

          setRequestedBefore("yes");
          setHasLoanAccount("yes");
          const loanNo = emailDetails["loannaumber.ibhfl_name"] || "";
          setLoanAccountNo(loanNo);
          setIsLoanVerified(true);
          setIsPrefilled(true);
        })
        .catch((err) => {
          setGlobalError(
              err?.message ||
              "Unable to fetch grievance details. Please try again.",
          );
        })
        .finally(() => setPrefillLoading(false));
  }, [caseIdFromUrl]);

  useEffect(() => {
    const fetchBankDetails = async () => {
      if (formData.queryType?.includes("cibil-related") && loanAccountNo) {
        try {
          const res = await getTrancheAndBankDetails(loanAccountNo);
          const bankInfo = res?.Payload?.bankInfo;

          if (bankInfo) {
            setBankName(bankInfo.bankname || "");
            setAccountNo(bankInfo.accountno || "");
          }
        } catch (err) {
          console.error("Bank API Error:", err);
        }
      } else {
        setBankName("");
        setAccountNo("");
      }
    };

    fetchBankDetails();
  }, [formData.queryType, loanAccountNo]);

  //resend otp impl
  useEffect(() => {

    const interval = setInterval(() => {
      setMobileOtpTimer((prev) => (prev > 0 ? prev - 1 : 0));
      setEmailOtpTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle flow question changes
  const redirectToWriteToUs = () => {
    const baseUrl = globalThis.Liferay?.ThemeDisplay?.getPortalURL() || "";
    globalThis.location.href = `${baseUrl}/${WRITE_TO_US_PAGE_URL}`;
  };

  const handleRequestedBeforeChange = (value) => {
    setRequestedBefore(value);
    if (value === "yes" && !caseIdFromUrl) {
      redirectToWriteToUs();
      return;
    }
    setGlobalError("");
  };

  const handleHasLoanAccountChange = (value) => {
    setHasLoanAccount(value);
    setIsLoanVerified(false);
    setLoanAccountNo("");
    setFormData((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      mobileNumber: "",
      email: "",
    }));
    setGlobalError("");
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      mobileNumber: "",
      email: "",
      citizenship: "indian",
      queryType: "",
      grievanceMessage: "",
      attachmentFile: null,
    });
    setCaptchaInput("");
    setIsCaptchaValid(false);
    setIsOtpVerified(false);
    setOtpTarget(null);
    setErrors({});
    setGlobalError("");
    setMobileOtpTimer(0);
    setEmailOtpTimer(0);
  };
  const extractApplicantData = (payload) => {
    const applicantList = payload?.applicantsDetail?.responseParameters || [];
    const addressList = payload?.addressDetail?.responseParameters || [];

    const primaryApplicant = applicantList.find(
        (a) => a.applicanttype === "Applicant"
    );

    if (!primaryApplicant) {
      throw new Error("Applicant details not found");
    }

    const primaryAddress =
        addressList.find(
            (a) => a.applicanttype === "Applicant" && a.addresstype === "CURRES"
        ) || addressList.find((a) => a.applicanttype === "Applicant");

    return {
      firstName: primaryApplicant.firstname || "",
      lastName: primaryApplicant.lastname || "",
      mobileNumber: primaryAddress?.mobile || "",
      email: primaryAddress?.emailid || "",
    };
  };

  const handleVerifyLoan = async () => {
    if (!loanAccountNo.trim()) {
      setGlobalError("Please enter a loan account number");
      return;
    }

    setLoanVerifyLoading(true);
    setGlobalError("");

    try {
      const res = await verifyLoanAccount(loanAccountNo);
      const payload = res?.Payload;

      if (!payload) {
        throw new Error("Invalid response from server");
      }

      const applicantData = extractApplicantData(payload);

      setFormData((prev) => ({
        ...prev,
        ...applicantData,
      }));

      setIsLoanVerified(true);
    } catch (error) {
      setGlobalError(error?.message || "Failed to verify loan account");
    } finally {
      setLoanVerifyLoading(false);
    }
  };

  // Send OTP to mobile
  const handleSendMobileOtp = async () => {
    if (mobileOtpTimer > 0) return;

    if (!formData.mobileNumber || !isValidMobile(formData.mobileNumber)) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "Please enter a valid 10-digit mobile number",
      }));
      return;
    }

    setOtpLoading(true);
    setGlobalError("");
    try {
      await sendMobileOtp(formData.mobileNumber);
      setOtpTarget("mobile");
      setShowOtpModal(true);   // open modal
    } catch (error) {
      setGlobalError(error.message);
    } finally {
      setOtpLoading(false);
    }
  };

  // Send OTP to email
  const handleSendEmailOtp = async () => {
    if (!formData.email || !emailRegex.test(formData.email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email address",
      }));
      return;
    }
    setOtpLoading(true);
    setGlobalError("");

    try {
      await sendEmailOtp(formData.email);
      setOtpTarget("email");
      setShowOtpModal(true);  // open modal
    } catch (error) {
      setGlobalError(error.message);
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtpFromModal = async (otp) => {
    try {
      const res = await verifyOtp({
        type: otpTarget,
        value: otpTarget === "mobile" ? formData.mobileNumber : formData.email,
        otp,
      });

      if (res?.Error?.ErrorCode) {
        throw new Error(res.Error.ErrorMessage || "Incorrect OTP");
      }

      setIsOtpVerified(true);
      setShowOtpModal(false);

      return true;
    } catch (error) {
      setIsOtpVerified(false);
      setGlobalError(`${error.message}_${Date.now()}`);
      return false;
    }
  };

  const resetOtpFlow = () => {
    setOtpTarget(null);
    setIsOtpVerified(false);
  };
  // Captcha validation
  const handleCaptchaChange = (inputValue, generatedValue) => {
    setCaptchaInput(inputValue);
    setIsCaptchaValid(
        inputValue.toLowerCase() === generatedValue.toLowerCase(),
    );
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    // Filter mobile number to only allow digits
    if (name === "mobileNumber") {
      updatedValue = value.replaceAll(/\D/g, "").slice(0, 10);
      setMobileOtpTimer(0);
      setGlobalError("");
    }

    if (name === "email") {
      setEmailOtpTimer(0);
      setGlobalError("");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    if ((name === "mobileNumber" && otpTarget === "mobile") ||(name === "email" && otpTarget === "email")) {
      resetOtpFlow();
    }

    // Clear error when typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle file upload
  const handleFileChange = (file) => {
    setFormData((prev) => ({ ...prev, attachmentFile: file }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {
      ...validatePersonalDetails(formData, {}),
      ...validateContactDetails(formData, {}),
      ...validateGrievanceDetails(formData, {}),
    };

    if (!isPrefilled) {
      validateCaptchaAndOtp(isCaptchaValid, isOtpVerified, newErrors);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const buildSubmitPayload = () => ({
    ...formData,
    serviceRequestNumber: caseIdFromUrl || "",
    requestedBefore,
    hasLoanAccount,
    loanAccountNo: hasLoanAccount === "yes" ? loanAccountNo : "",
    bankName,
    bankAccountNumber: accountNo,
    isOtpVerified,
  });
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setGlobalError("");

    try {
      const submitData = buildSubmitPayload();
      await submitGrievance(submitData, siteId);
      setSubmitSuccess(true);
    } catch (error) {
      setGlobalError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success view
  if (submitSuccess) {
    return (
        <SuccessView
            onReset={() => {
              setSubmitSuccess(false);
              setRequestedBefore(null);
              setHasLoanAccount(null);
              resetForm();
            }}
        />
    );
  }
  const shouldShowMainForm =
      hasLoanAccount === "no" || (hasLoanAccount === "yes" && isLoanVerified);
  return (
      <div className="scl-grievance-form">
        {/* <h1 className="scl-grievance-form__title">Let us Know About Your Grievance</h1> */}

        {globalError && (
            <div className="scl-form-error-message">{getCleanErrorMessage(globalError)}</div>
        )}

        {prefillLoading && (
            <div className="scl-form-loading">Loading grievance details...</div>
        )}

        <div className="scl-form-container">
          <form onSubmit={handleSubmit}>
            {/* Question 1 */}
            {!caseIdFromUrl && (
                <div className="scl-grievance-form__question">
                  <label htmlFor="appliedEarlier" className="scl-grievance-form__question-label small-body neutral1000">
                    Have you requested for the same grievance earlier?
                  </label>

                  <div className="scl-grievance-form__toggle-group">
                    <button
                        id="appliedEarlier"
                        type="button"
                        className={`scl-grievance-form__toggle-btn ${
                            requestedBefore === "yes"
                                ? "scl-grievance-form__toggle-btn--active"
                                : ""
                        }`}
                        onClick={() => handleRequestedBeforeChange("yes")}
                    >
                      Yes
                    </button>

                    <button
                        type="button"
                        className={`scl-grievance-form__toggle-btn ${
                            requestedBefore === "no"
                                ? "scl-grievance-form__toggle-btn--active"
                                : ""
                        }`}
                        onClick={() => handleRequestedBeforeChange("no")}
                    >
                      No
                    </button>
                  </div>
                </div>
            )}

            {(requestedBefore === "no" || isPrefilled) && (
                <>
                  {/* Question 2 */}
                  <div className="scl-grievance-form__question">
                    <label htmlFor="haveLoanAccount" className="scl-grievance-form__question-label small-body neutral1000">
                      Do you have a Loan Account?
                    </label>

                    <div className="scl-grievance-form__toggle-group">
                      <button
                          id="haveLoanAccount"
                          type="button"
                          className={`scl-grievance-form__toggle-btn ${
                              hasLoanAccount === "yes"
                                  ? "scl-grievance-form__toggle-btn--active"
                                  : ""
                          }`}
                          onClick={() => handleHasLoanAccountChange("yes")}
                      >
                        Yes
                      </button>

                      <button
                          type="button"
                          className={`scl-grievance-form__toggle-btn ${
                              hasLoanAccount === "no"
                                  ? "scl-grievance-form__toggle-btn--active"
                                  : ""
                          }`}
                          onClick={() => handleHasLoanAccountChange("no")}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  {/* Loan Account Verification */}
                  {hasLoanAccount === "yes" && !isLoanVerified && (
                      <div className="scl-grievance-form__question">
                        <label htmlFor="loanAccountNo" className="scl-grievance-form__question-label small-body neutral1000">
                          Loan Account Number
                        </label>

                        <div className="input-box">
                          <input
                              id="loanAccountNo"
                              type="text"
                              name="loanAccountNo"
                              value={loanAccountNo}
                              onChange={(e) => setLoanAccountNo(e.target.value)}
                              placeholder="Loan Account No."
                              className="scl-form-input"
                          />

                          <button
                              type="button"
                              className="scl-form-inline-btn"
                              onClick={handleVerifyLoan}
                              disabled={loanVerifyLoading || !loanAccountNo.trim()}
                          >
                            {loanVerifyLoading ? "Verifying..." : "Verify"}
                          </button>
                        </div>
                      </div>
                  )}

                  {/* Main Form */}
                  {shouldShowMainForm && (
                      <div className="scl-grievance-form__main-form">
                        <div className="scl-form-row">
                          {/* First Name */}
                          <div className="scl-form-group scl-form-group--half scl-grievance-form__question">
                            <label htmlFor="firstName" className="scl-grievance-form__question-label small-body neutral1000">
                              First Name
                            </label>

                            <input
                                id="firstName"
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                placeholder="First Name"
                                className={`scl-form-input ${
                                    errors.firstName ? "scl-form-input--error" : ""
                                }`}
                            />

                            {errors.firstName && (
                                <span className="scl-form-field-error">
                          {errors.firstName}
                        </span>
                            )}
                          </div>

                          {/* Last Name */}
                          <div className="scl-form-group scl-form-group--half scl-grievance-form__question">
                            <label htmlFor="lastName" className="scl-grievance-form__question-label small-body neutral1000">
                              Last Name
                            </label>

                            <input
                                id="lastName"
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                placeholder="Last Name"
                                className={`scl-form-input ${
                                    errors.lastName ? "scl-form-input--error" : ""
                                }`}
                            />

                            {errors.lastName && (
                                <span className="scl-form-field-error">
                          {errors.lastName}
                        </span>
                            )}
                          </div>
                        </div>

                        {/* Mobile */}
                        <div className="scl-form-group scl-grievance-form__question">
                          <label htmlFor="mobileNumber" className="scl-grievance-form__question-label small-body neutral1000">
                            Mobile Number
                          </label>

                          <div className="scl-split-input">
                            {/* Country Code Section */}
                            <span className="scl-split-input__action scl-split-input__prefix">
                        <img
                            src="/documents/d/global/flag-of-india"
                            alt="India"
                            width={24}
                            height={16}
                        />{" "}
                        +91
                      </span>

                            {/* Phone Input */}
                            <input
                                id="mobileNumber"
                                type="tel"
                                name="mobileNumber"
                                value={formData.mobileNumber}
                                onChange={handleInputChange}
                                placeholder="e.g. 98762 72383"
                                className={`scl-split-input__field ${
                                    errors.mobileNumber ? "scl-form-input--error" : ""
                                }`}
                                maxLength={10}
                                inputMode="numeric"
                                pattern="[0-9]*"
                            />

                            {/* SEND OTP */}
                            {otpTarget !== "email" && (
                                <button
                                  type="button"
                                  className={`scl-otp-btn scl-form-inline-btn ${
                                    isOtpVerified && otpTarget === "mobile" ? "verified" : ""
                                  }`}
                                  onClick={handleSendMobileOtp}
                                  disabled={!isValidMobile(formData.mobileNumber) || (isOtpVerified && otpTarget === "mobile")}
                                >
                                  {isOtpVerified && otpTarget === "mobile" ? (
                                    <>
                                      <img
                                        src="/documents/d/global/check-circle-green-tick"
                                        width="16"
                                        height="16"
                                        alt="verified"
                                      />
                                      Verified
                                    </>
                                  ) : (
                                    "Send OTP"
                                  )}
                                </button>
                            )}
                          </div>

                          {errors.mobileNumber && (
                              <span className="scl-form-field-error">
                        {errors.mobileNumber}
                      </span>
                          )}

                          <div className="scl-form-field-info">
                            <img
                                src="/documents/d/global/form-info"
                                width={16}
                                height={16}
                                alt="info"
                            />
                            <span>
                        We’ll verify your number for safety & personalization.
                      </span>
                          </div>
                        </div>

                        {/* Email */}
                        <div className="scl-form-group scl-grievance-form__question">
                          <label htmlFor="email" className="scl-grievance-form__question-label small-body neutral1000">
                            Email
                          </label>

                          <div className="input-box">
                            <input
                                id="email"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Email"
                                className={`scl-form-input ${
                                    errors.email ? "scl-form-input--error" : ""
                                }`}
                            />

                            {otpTarget !== "mobile" && (
                                <button
                                  type="button"
                                  className={`scl-otp-btn scl-form-inline-btn ${
                                    isOtpVerified && otpTarget === "email" ? "verified" : ""
                                  }`}
                                  onClick={handleSendEmailOtp}
                                  disabled={!emailRegex.test(formData.email) ||(isOtpVerified && otpTarget === "email")}
                                >
                                  {isOtpVerified && otpTarget === "email" ? (
                                    <>
                                      <img
                                        src="/documents/d/global/check-circle-green-tick"
                                        width="16"
                                        height="16"
                                        alt="verified"
                                      />
                                      Verified
                                    </>
                                  ) : (
                                    "Send OTP"
                                  )}
                                </button>
                            )}
                          </div>

                          {errors.email && (<span className="scl-form-field-error">{errors.email}</span>)}

                          <div className="scl-form-field-info">
                            <img
                                src="/documents/d/global/form-info"
                                width={16}
                                height={16}
                                alt="info"
                            />
                            <span>
                        We’ll verify your email for safety & personalization.
                      </span>
                          </div>
                        </div>



                        {/* Citizenship */}
                        <div className="scl-grievance-form__question">
                          <label htmlFor="citizenship" className="scl-grievance-form__question-label small-body neutral1000">
                            Citizenship
                          </label>

                          <div className="scl-grievance-form__toggle-group">
                            {CITIZENSHIP_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`scl-grievance-form__toggle-btn ${
                                        formData.citizenship === option.value
                                            ? "scl-grievance-form__toggle-btn--active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          citizenship: option.value,
                                        }))
                                    }
                                >
                                  {option.label}
                                </button>
                            ))}
                          </div>
                        </div>

                        {/* Query Type */}
                        <div className="scl-grievance-form__question">
                          <label htmlFor="queryType" className="scl-grievance-form__question-label small-body neutral1000">
                            Query Type
                          </label>

                          <select
                              id="queryType"
                              name="queryType"
                              value={formData.queryType}
                              onChange={handleInputChange}
                              className={`scl-form-select ${
                                  errors.queryType ? "scl-form-select--error" : ""
                              }`}
                          >
                            <option value="">Select query type</option>

                            {GRIEVANCE_QUERY_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                            ))}
                          </select>

                          {errors.queryType && (
                              <span className="scl-form-field-error">
                        {errors.queryType}
                      </span>
                          )}
                        </div>

                        {/* Grievance Message */}
                        <div className="scl-grievance-form__question">
                          <label htmlFor="writeYourGrievance" className="scl-grievance-form__question-label small-body neutral1000">
                            Write your Grievance
                          </label>

                          <textarea
                              id="writeYourGrievance"
                              name="grievanceMessage"
                              value={formData.grievanceMessage}
                              onChange={handleInputChange}
                              placeholder="Write Your Grievance Here...."
                              className={`scl-form-textarea ${
                                  errors.grievanceMessage
                                      ? "scl-form-textarea--error"
                                      : ""
                              }`}
                              rows={5}
                          />

                          {errors.grievanceMessage && (
                              <span className="scl-form-field-error">
                        {errors.grievanceMessage}
                      </span>
                          )}
                        </div>

                        <div className="scl-grievance-form__question">
                          <label htmlFor="file" className="scl-grievance-form__question-label">
                            File
                          </label>

                          <div className="scl-split-input">
                            <label htmlFor="uploadfile" className="scl-split-input__action">
                              <img
                                  src="/documents/d/global/down-chevron-2-"
                                  alt="upload"
                                  height={14}
                                  width={14}
                              />{" "}
                              Upload
                              <input
                                  id="uploadfile"
                                  type="file"
                                  className="scl-file-hidden"
                                  accept=".pdf,.png,.jpg,.jpeg"
                                  onChange={(e) => handleFileChange(e.target.files[0])}
                              />
                            </label>

                            {formData.attachmentFile ? (
                                <span className="scl-split-input__field">
                          {formData.attachmentFile.name}
                        </span>
                            ) : (
                                <span className="scl-split-input__placeholder">
                          Max. 2 MB size in pdf, png, jpg, jpeg format
                        </span>
                            )}
                          </div>

                          {errors.attachmentFile && (
                              <span className="scl-form-field-error">
                        {errors.attachmentFile}
                      </span>
                          )}
                        </div>

                        <div className="scl-grievance-form__question">
                          <label htmlFor="captcha" className="scl-grievance-form__question-label">
                            Captcha
                          </label>

                          <CaptchaSection
                              value={captchaInput}
                              onChange={handleCaptchaChange}
                              error={errors.captcha}
                          />
                        </div>
                      </div>
                  )}
                </>
            )}

            {showOtpModal && (
                <OtpModal
                    phoneNumber={
                      otpTarget === "mobile" ? formData.mobileNumber : formData.email
                    }
                    onSendOtp={
                      otpTarget === "mobile" ? handleSendMobileOtp : handleSendEmailOtp
                    }
                    onVerifyOtp={handleVerifyOtpFromModal}
                    otpError={globalError}
                    onClose={() => {
                      setShowOtpModal(false);
                    }}
                />
            )}
           {shouldShowMainForm && (
            <div className="scl-form-actions">
              <button
                  type="submit"
                  className="scl-form-btn--primary btn primary-button d-inline"
                  disabled={isSubmitting || !isOtpVerified || !isCaptchaValid}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
           )}
          </form>
        </div>
      </div>
  );
}
GrievanceFormApp.propTypes = {
  siteId: PropTypes.string,
};
export default GrievanceFormApp;
